import { useEffect, useMemo, useState, type FC, type PropsWithChildren } from "react";
import { OnboardingContext } from "@/contexts/OnboardingContext";
import { getPrincipal, onDidChangePrincipal } from "@/principal";
import { type OnboardingStepId } from "@/hooks/use-onboarding";
import { useOrganizationOnboardingSteps } from "@/hooks/use-organization-onboarding-steps";

export const OnboardingProvider: FC<PropsWithChildren> = ({ children }) => {
    const steps = useOrganizationOnboardingSteps();

    // There are some scenarios where we want to mark a step as completed based on user activity
    // that isn't covered by the heuristics, e.g. when clicking "next" on the first screen
    const [inMemoryOverrides, setInMemoryOverrides] = useState<Partial<Record<OnboardingStepId, boolean>>>({});

    // This ensures that we trigger a re-render when the principal changes - this is needed to
    // ensure we re-render when the user is updated.
    const [, setPrincipal] = useState<string | null>(null);
    useEffect(() => {
        return onDidChangePrincipal(() => {
            setPrincipal(getPrincipal());
        });
    }, [setPrincipal]);

    const complete = useMemo(() => {
        return (step: OnboardingStepId) => {
            setInMemoryOverrides((prev) => ({ ...prev, [step]: true }));
        };
    }, []);

    const stepsWithOverrides = useMemo(() => {
        return steps.steps.map((s) => {
            if (inMemoryOverrides[s.id] ?? false) {
                return { ...s, completed: true };
            }
            return s;
        });
    }, [steps.steps, inMemoryOverrides]);

    return (
        <OnboardingContext.Provider
            value={{
                steps: {
                    defaultActive: steps.defaultActive,
                    isLoading: steps.isLoading,
                    steps: stepsWithOverrides,
                },
                complete,
            }}
        >
            {children}
        </OnboardingContext.Provider>
    );
};
